import { fetchAuthSession } from 'aws-amplify/auth';

import config from 'aws-exports';

/**
 * REST client for the AOR mappings + runs routes (#33089). Reads carry the shared X-Api-Key, writes
 * add the Cognito bearer token. No cache-bust cookie needed (unlike the affinities tab) — both route
 * families have zero-TTL CloudFront behaviours.
 */
const aorAffinitiesEndpoint = `${config.coreResourcesEndpoint}/aor-affinities`;
const aorRunsEndpoint = `${config.coreResourcesEndpoint}/aor-runs`;

const commonHeaders = {
  'X-Api-Key': config.coreResourcesApiKey,
  Accept: 'application/json'
};

const getToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const text = await response.text();
    let errorDetail = '';
    let body = null;
    try {
      body = JSON.parse(text);
      errorDetail = body.error ? `- ${body.error}` : '';
    } catch {
      errorDetail = text ? `- ${text}` : '';
    }
    const error = new Error(`${response.status} (${response.statusText}) ${errorDetail}`);
    // The approve dialog renders the server's own wording and branches on staleReason, so keep both
    // alongside the prefixed message the other callers already show.
    error.httpStatus = response.status;
    error.detail = body?.error ?? text ?? null;
    error.staleReason = body?.staleReason ?? null;
    error.runStatus = body?.status ?? null;
    throw error;
  }

  return response.json();
};

/** Full active old -> new map; the mappings tab filters client-side. */
export const aorMappingsRequest = async () => {
  try {
    const response = await fetch(aorAffinitiesEndpoint, { headers: commonHeaders });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(`Error fetching AOR mappings: ${error.message}`);
  }
};

/** One mapping by its FORMER code — resolves the request id for a first-ever preview. */
export const aorMappingByAffinityRequest = async (affinity) => {
  try {
    const response = await fetch(`${aorAffinitiesEndpoint}/${encodeURIComponent(affinity)}`, {
      headers: commonHeaders
    });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(`Error fetching the AOR mapping for ${affinity}: ${error.message}`);
  }
};

/** The set a preview would enumerate against. */
export const aorMappingsByRequestRequest = async (aorRequestId) => {
  try {
    const params = new URLSearchParams({ aorRequestId });
    const response = await fetch(`${aorAffinitiesEndpoint}?${params.toString()}`, { headers: commonHeaders });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(`Error fetching mappings for AOR request: ${error.message}`);
  }
};

export const createAorMappingRequest = async ({ newAffinity, oldAffinities, notes, createdBy }) => {
  try {
    const token = await getToken();
    const response = await fetch(aorAffinitiesEndpoint, {
      method: 'POST',
      headers: { ...commonHeaders, 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ newAffinity, oldAffinities, notes, createdBy })
    });

    return await handleResponse(response);
  } catch (error) {
    throw new Error(`Error creating AOR mapping: ${error.message}`);
  }
};

/** Logical delete — the row is kept with status 'deleted' so history survives. */
export const deleteAorMappingRequest = async ({ oldAffinity, updatedBy }) => {
  try {
    const token = await getToken();
    const response = await fetch(`${aorAffinitiesEndpoint}/${encodeURIComponent(oldAffinity)}`, {
      method: 'DELETE',
      headers: { ...commonHeaders, 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ updatedBy })
    });

    return await handleResponse(response);
  } catch (error) {
    throw new Error(`Error deleting AOR mapping: ${error.message}`);
  }
};

/** One run, isStale derived server-side on read — the run detail page's data source. */
export const aorRunRequest = async (runId) => {
  try {
    const response = await fetch(`${aorRunsEndpoint}/${encodeURIComponent(runId)}`, { headers: commonHeaders });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(`Error fetching AOR run ${runId}: ${error.message}`);
  }
};

export const aorRunsRequest = async ({ aorRequestId, affinity, status, limit, offset, hideSupersededStale } = {}) => {
  try {
    const params = new URLSearchParams();
    if (aorRequestId) {
      params.set('aorRequestId', aorRequestId);
    }
    if (affinity) {
      params.set('affinity', affinity);
    }
    if (status) {
      params.set('status', status);
    }
    if (limit) {
      params.set('limit', String(limit));
    }
    if (offset) {
      params.set('offset', String(offset));
    }
    // Only the literal 'true' activates it server-side, and the exclusion runs inside the same filter
    // builder as the COUNT — which is why the pager stays correct and this cannot be done client-side.
    if (hideSupersededStale) {
      params.set('hideSupersededStale', 'true');
    }

    const queryStr = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${aorRunsEndpoint}${queryStr}`, { headers: commonHeaders });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(`Error fetching AOR runs: ${error.message}`);
  }
};

/**
 * The approving user comes from token claims server-side, deliberately not the body. A 409 is an
 * expected outcome (already approved, or the snapshot went stale) — surface it, don't retry.
 *
 * Approval also resumes the parked machine, so this one call carries the apply inputs (repUsername,
 * updateType) and its response is the first place the apply task run id exists. Deliberately NOT
 * wrapped in a re-thrown Error: that would drop the staleReason and detail the dialog renders.
 */
export const approveAorRunRequest = async ({ runId, repUsername, updateType, notes }) => {
  const token = await getToken();
  const response = await fetch(`${aorRunsEndpoint}/${encodeURIComponent(runId)}/approve`, {
    method: 'POST',
    headers: { ...commonHeaders, 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ repUsername, updateType, notes })
  });

  return handleResponse(response);
};
